import { useEffect, useRef, useMemo, useCallback } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import * as THREE from 'three'
import { sampleTerrainRadius } from '@/world/TerrainSphere'
import { updatePlayerPosition } from '@/state/playerPosition'
import { useShipStore } from '@/state/useShipStore'
import { SHIP_TIERS } from './shipTypes'
import { ShipMesh } from './ShipMesh'
import { Cannonball } from './Cannonball'
import { sampleOceanHeight } from './oceanHeight'
import { createShipMaterial } from './shipShader'
import { getDockPlacements } from '@/interaction/InteractionDetector'

const MOUSE_SENSITIVITY = 0.002
const PITCH_MIN = -0.2
const PITCH_MAX = 1.0

const CAM_POS_LERP = 200  // Near-instant — prevents wave-bob lag
const CAM_ROT_LERP = 12

// Clamp phi away from poles
const PHI_MIN = 0.01
const PHI_MAX = Math.PI - 0.01

// Pre-allocated temporaries
const _normal = new THREE.Vector3()
const _shipPos = new THREE.Vector3()
const _camOffset = new THREE.Vector3()
const _fwd = new THREE.Vector3()
const _right = new THREE.Vector3()
const _camFwd = new THREE.Vector3()
const _q = new THREE.Quaternion()
const _yawQ = new THREE.Quaternion()
const _targetCamPos = new THREE.Vector3()
const _targetCamQ = new THREE.Quaternion()
const _lookAtMatrix = new THREE.Matrix4()

const UP = new THREE.Vector3(0, 1, 0)

export interface CannonballState {
  id: number
  position: THREE.Vector3
  velocity: THREE.Vector3
  age: number
  side: 'port' | 'starboard'
}

const DOCK_RANGE = 80 // units — how close to a dock to allow disembarking

interface ShipControllerProps {
  sphereRadius: number
  seed?: number
  sunPosition?: THREE.Vector3
  onDisembark: (theta: number, phi: number, dockIndex: number) => void
}

let _cannonballId = 0

export function ShipController({
  sphereRadius,
  seed = 42,
  sunPosition,
  onDisembark,
}: ShipControllerProps) {
  const { camera, gl } = useThree()
  const canvas = gl.domElement
  const lightRef = useRef<THREE.Group>(null)

  const activeShip = useShipStore((s) => s.getActiveShip())
  const updateShipPosition = useShipStore((s) => s.updateShipPosition)
  const setPortCooldown = useShipStore((s) => s.setPortCooldown)
  const setStarboardCooldown = useShipStore((s) => s.setStarboardCooldown)

  const thetaRef = useRef(activeShip?.theta ?? 0)
  const phiRef = useRef(activeShip?.phi ?? 1.0)
  const headingRef = useRef(activeShip?.heading ?? 0)

  const camYawRef = useRef(0)
  const camPitchRef = useRef(0.3)
  const speedRef = useRef(0)
  const yawRateRef = useRef(0)
  const nearDockRef = useRef(-1) // dock index, or -1 if not near any dock
  const portCdRef = useRef(0)
  const starboardCdRef = useRef(0)
  const cannonballsRef = useRef<CannonballState[]>([])
  const keysRef = useRef({ w: false, s: false, a: false, d: false, q: false, e: false })
  const lockedRef = useRef(false)
  const smoothPitchRef = useRef(0)
  const smoothRollRef = useRef(0)

  // Ship material — buoyancy probes computed on CPU, passed as uniforms
  const material = useMemo(() => createShipMaterial(sphereRadius), [sphereRadius])

  // Initialize position from ship data
  useEffect(() => {
    if (activeShip) {
      thetaRef.current = activeShip.theta
      phiRef.current = activeShip.phi
      headingRef.current = activeShip.heading
    }
  }, [activeShip?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleClick = useCallback(() => {
    if (!lockedRef.current) canvas.requestPointerLock()
  }, [canvas])

  const handleLockChange = useCallback(() => {
    lockedRef.current = document.pointerLockElement === canvas
  }, [canvas])

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!lockedRef.current) return
    camYawRef.current -= e.movementX * MOUSE_SENSITIVITY
    camPitchRef.current += e.movementY * MOUSE_SENSITIVITY
    camPitchRef.current = Math.max(PITCH_MIN, Math.min(PITCH_MAX, camPitchRef.current))
  }, [])

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    const key = e.key.toLowerCase()
    if (key === 'w' || key === 'arrowup') keysRef.current.w = true
    if (key === 's' || key === 'arrowdown') keysRef.current.s = true
    if (key === 'a' || key === 'arrowleft') keysRef.current.a = true
    if (key === 'd' || key === 'arrowright') keysRef.current.d = true
    if (key === 'q') keysRef.current.q = true
    if (key === 'e') keysRef.current.e = true
    if (key === 'f' && nearDockRef.current >= 0) {
      onDisembark(thetaRef.current, phiRef.current, nearDockRef.current)
    }
  }, [onDisembark])

  const handleKeyUp = useCallback((e: KeyboardEvent) => {
    const key = e.key.toLowerCase()
    if (key === 'w' || key === 'arrowup') keysRef.current.w = false
    if (key === 's' || key === 'arrowdown') keysRef.current.s = false
    if (key === 'a' || key === 'arrowleft') keysRef.current.a = false
    if (key === 'd' || key === 'arrowright') keysRef.current.d = false
    if (key === 'q') keysRef.current.q = false
    if (key === 'e') keysRef.current.e = false
    if (key === 'escape') {
      if (lockedRef.current) document.exitPointerLock()
    }
  }, [])

  useEffect(() => {
    camera.near = 0.5
    camera.far = 30_000
    ;(camera as THREE.PerspectiveCamera).updateProjectionMatrix()
  }, [camera])

  useEffect(() => {
    canvas.addEventListener('click', handleClick)
    document.addEventListener('pointerlockchange', handleLockChange)
    document.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('keyup', handleKeyUp)

    return () => {
      canvas.removeEventListener('click', handleClick)
      document.removeEventListener('pointerlockchange', handleLockChange)
      document.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('keyup', handleKeyUp)
      if (lockedRef.current) document.exitPointerLock()
    }
  }, [canvas, handleClick, handleLockChange, handleMouseMove, handleKeyDown, handleKeyUp])

  // Priority -1: run before ocean's useFrame (priority 0) so camera
  // position is finalized before ocean reads it for camera-relative rendering.
  useFrame((state, delta) => {
    if (!activeShip) return

    const tierInfo = SHIP_TIERS[activeShip.tier]
    const dt = Math.min(delta, 0.1)
    const time = state.clock.elapsedTime

    // ── Wind ──
    const maxSpeed = tierInfo.speed
    const windDir = time * (2 * Math.PI / 300) // wind rotates fully every 5 min
    let relWind = headingRef.current - windDir
    relWind = ((relWind + Math.PI) % (2 * Math.PI) + 2 * Math.PI) % (2 * Math.PI) - Math.PI
    const headingToWind = Math.abs(relWind) // 0 = into wind, PI = downwind
    // Interpolate wind profile: [upwind, beam, downwind] at 0, PI/2, PI
    const wp = tierInfo.windProfile
    const wt = headingToWind / (Math.PI / 2) // 0..2
    const windFactor = wt <= 1
      ? wp[0] + (wp[1] - wp[0]) * wt
      : wp[1] + (wp[2] - wp[1]) * (wt - 1)

    // ── Quadratic drag + thrust ──
    const thrust = keysRef.current.w
      ? tierInfo.thrust * windFactor
      : keysRef.current.s ? -tierInfo.thrust * 0.3 : 0
    const drag = tierInfo.dragCoeff * speedRef.current * Math.abs(speedRef.current)
    speedRef.current += (thrust - drag) * dt
    // Clamp reverse speed
    if (speedRef.current < -maxSpeed * 0.3) speedRef.current = -maxSpeed * 0.3

    // ── Turning: bell-curve turn rate + inertia ──
    const speedRatio = Math.min(Math.abs(speedRef.current) / maxSpeed, 1)
    const turnMultiplier = Math.pow(speedRatio, 0.5) * (1.4 - 0.4 * speedRatio)
    const inputDir = (keysRef.current.a ? 1 : 0) - (keysRef.current.d ? 1 : 0)
    const targetYawRate = inputDir * tierInfo.turnRate * turnMultiplier
    // Smoothed turn inertia
    yawRateRef.current += (targetYawRate - yawRateRef.current) * (1 - Math.exp(-tierInfo.turnAccel * dt))
    headingRef.current += yawRateRef.current * dt

    // ── Speed loss in turns ──
    speedRef.current -= Math.abs(yawRateRef.current) * speedRef.current * 0.12 * dt

    // ── Move on sphere surface ──
    const sinPhi = Math.sin(phiRef.current)
    const cosPhi = Math.cos(phiRef.current)
    const sinTheta = Math.sin(thetaRef.current)
    const cosTheta = Math.cos(thetaRef.current)

    const nx = sinPhi * sinTheta
    const ny = cosPhi
    const nz = sinPhi * cosTheta

    const eastX = cosTheta, eastZ = -sinTheta
    const northX = cosPhi * sinTheta, northY = -sinPhi, northZ = cosPhi * cosTheta

    if (Math.abs(speedRef.current) > 0.01) {
      const cosH = Math.cos(headingRef.current)
      const sinH = Math.sin(headingRef.current)

      const fwdX = -northX * cosH + eastX * sinH
      const fwdY = -northY * cosH
      const fwdZ = -northZ * cosH + eastZ * sinH

      const angularStep = (speedRef.current * dt) / sphereRadius
      let newX = nx + fwdX * angularStep
      let newY = ny + fwdY * angularStep
      let newZ = nz + fwdZ * angularStep
      const newLen = Math.sqrt(newX * newX + newY * newY + newZ * newZ)
      newX /= newLen; newY /= newLen; newZ /= newLen

      const terrainR = sampleTerrainRadius(newX, newY, newZ, sphereRadius, seed)
      if (terrainR <= sphereRadius * 1.001) {
        const newPhi = Math.acos(Math.max(-1, Math.min(1, newY)))
        phiRef.current = Math.max(PHI_MIN, Math.min(PHI_MAX, newPhi))
        thetaRef.current = Math.atan2(newX, newZ)

        // Parallel transport heading
        const newSP = Math.sin(phiRef.current)
        const newCP = Math.cos(phiRef.current)
        const newST = Math.sin(thetaRef.current)
        const newCT = Math.cos(thetaRef.current)

        const newEastX = newCT, newEastZ = -newST
        const newNorthX = newCP * newST, newNorthY = -newSP, newNorthZ = newCP * newCT

        const newSinH = fwdX * newEastX + fwdZ * newEastZ
        const newCosH = -(fwdX * newNorthX + fwdY * newNorthY + fwdZ * newNorthZ)
        headingRef.current = Math.atan2(newSinH, newCosH)
      } else {
        speedRef.current = 0
      }
    }

    // ── Ship orientation + CPU buoyancy probes ──
    const curST = Math.sin(thetaRef.current), curCT = Math.cos(thetaRef.current)
    const curSP = Math.sin(phiRef.current), curCP = Math.cos(phiRef.current)
    const cosH = Math.cos(headingRef.current), sinH = Math.sin(headingRef.current)

    const cnx = curSP * curST
    const cny = curCP
    const cnz = curSP * curCT

    _normal.set(cnx, cny, cnz)

    _fwd.set(
      -(curCP * curST) * cosH + curCT * sinH,
      curSP * cosH,
      -(curCP * curCT) * cosH + (-curST) * sinH,
    )
    _right.crossVectors(_fwd, _normal)

    // 5-point buoyancy probes: center, bow, stern, port, starboard
    // Decoupled axes — pitch from bow/stern, roll from port/starboard
    const hCenter = sampleOceanHeight(cnx, cny, cnz, time)

    const probeBow = tierInfo.hullLength * 0.45
    const probeAft = tierInfo.hullLength * 0.4
    const probeBeam = tierInfo.hullWidth * 0.5

    // Bow probe
    const angBow = probeBow / sphereRadius
    let bowX = cnx + _fwd.x * angBow, bowY = cny + _fwd.y * angBow, bowZ = cnz + _fwd.z * angBow
    const bowL = Math.sqrt(bowX * bowX + bowY * bowY + bowZ * bowZ)
    bowX /= bowL; bowY /= bowL; bowZ /= bowL
    const hBow = sampleOceanHeight(bowX, bowY, bowZ, time)

    // Stern probe
    const angAft = probeAft / sphereRadius
    let stnX = cnx - _fwd.x * angAft, stnY = cny - _fwd.y * angAft, stnZ = cnz - _fwd.z * angAft
    const stnL = Math.sqrt(stnX * stnX + stnY * stnY + stnZ * stnZ)
    stnX /= stnL; stnY /= stnL; stnZ /= stnL
    const hStern = sampleOceanHeight(stnX, stnY, stnZ, time)

    // Port probe (left = -right)
    const angBeam = probeBeam / sphereRadius
    let ptX = cnx - _right.x * angBeam, ptY = cny - _right.y * angBeam, ptZ = cnz - _right.z * angBeam
    const ptL = Math.sqrt(ptX * ptX + ptY * ptY + ptZ * ptZ)
    ptX /= ptL; ptY /= ptL; ptZ /= ptL
    const hPort = sampleOceanHeight(ptX, ptY, ptZ, time)

    // Starboard probe (right)
    let sbX = cnx + _right.x * angBeam, sbY = cny + _right.y * angBeam, sbZ = cnz + _right.z * angBeam
    const sbL = Math.sqrt(sbX * sbX + sbY * sbY + sbZ * sbZ)
    sbX /= sbL; sbY /= sbL; sbZ /= sbL
    const hStarboard = sampleOceanHeight(sbX, sbY, sbZ, time)

    // Pitch from stern vs bow (independent of roll noise)
    let targetPitch = Math.atan2(hStern - hBow, probeBow + probeAft)
    // Roll from port vs starboard (independent of pitch noise)
    let targetRoll = Math.atan2(hPort - hStarboard, probeBeam * 2)
    // Dynamic heel: proportional to yaw rate and speed
    const turnHeel = -yawRateRef.current * (speedRef.current / maxSpeed) * 0.12
    targetRoll += turnHeel
    targetPitch = Math.max(-0.15, Math.min(0.15, targetPitch))
    targetRoll = Math.max(-0.15, Math.min(0.15, targetRoll))

    // Temporal smoothing — exponential lerp for stable, non-jerky tilt
    const smoothFactor = 1 - Math.exp(-4 * dt)
    smoothPitchRef.current += (targetPitch - smoothPitchRef.current) * smoothFactor
    smoothRollRef.current += (targetRoll - smoothRollRef.current) * smoothFactor
    const pitch = smoothPitchRef.current
    const roll = smoothRollRef.current

    // Raise ship slightly above waterline so wave surface doesn't clip the hull
    const draft = tierInfo.hullHeight * 0.2
    const shipH = hCenter + draft
    const shipR = sphereRadius + shipH
    _shipPos.copy(_normal).multiplyScalar(shipR)

    // ── Check dock proximity for disembarking ──
    nearDockRef.current = -1
    const docks = getDockPlacements()
    for (let i = 0; i < docks.length; i++) {
      if (_shipPos.distanceTo(docks[i].position) < DOCK_RANGE) {
        nearDockRef.current = i
        break
      }
    }

    // ── GPU uniforms (buoyancy pre-computed on CPU) ──
    const u = material.uniforms
    u.uShipTheta.value = thetaRef.current
    u.uShipPhi.value = phiRef.current
    u.uShipHeading.value = headingRef.current
    u.uOceanHeight.value = shipH
    u.uPitch.value = pitch
    u.uRoll.value = roll

    if (sunPosition) {
      u.uSunDirection.value.copy(sunPosition).normalize()
      u.uSunHeight.value = u.uSunDirection.value.dot(_normal)
    }

    updatePlayerPosition(_shipPos, _normal, thetaRef.current, phiRef.current)
    updateShipPosition(thetaRef.current, phiRef.current, headingRef.current)

    // ── Position lantern light ──
    if (lightRef.current) {
      _q.setFromUnitVectors(UP, _normal)
      _yawQ.setFromAxisAngle(_normal, -headingRef.current)
      _q.premultiply(_yawQ)
      lightRef.current.position.copy(_shipPos)
      lightRef.current.quaternion.copy(_q)
    }

    // ── Cannon cooldowns ──
    if (portCdRef.current > 0) {
      portCdRef.current = Math.max(0, portCdRef.current - dt)
      setPortCooldown(portCdRef.current)
    }
    if (starboardCdRef.current > 0) {
      starboardCdRef.current = Math.max(0, starboardCdRef.current - dt)
      setStarboardCooldown(starboardCdRef.current)
    }

    // ── Fire cannons ──
    if (keysRef.current.q && portCdRef.current <= 0) {
      fireCannons('port', tierInfo, _shipPos, _normal, _fwd)
      portCdRef.current = tierInfo.cannonCooldown
      setPortCooldown(tierInfo.cannonCooldown)
    }
    if (keysRef.current.e && starboardCdRef.current <= 0) {
      fireCannons('starboard', tierInfo, _shipPos, _normal, _fwd)
      starboardCdRef.current = tierInfo.cannonCooldown
      setStarboardCooldown(tierInfo.cannonCooldown)
    }

    // ── Camera ──
    {
      const totalYaw = headingRef.current + camYawRef.current
      const cosY = Math.cos(totalYaw), sinY = Math.sin(totalYaw)

      _camFwd.set(
        -(curCP * curST) * cosY + curCT * sinY,
        curSP * cosY,
        -(curCP * curCT) * cosY + (-curST) * sinY,
      )

      const camDist = tierInfo.hullLength * 10
      const camHeight = tierInfo.hullHeight * 13

      const cosPitch = Math.cos(camPitchRef.current)
      const sinPitch = Math.sin(camPitchRef.current)

      _camOffset.copy(_camFwd).multiplyScalar(-camDist * cosPitch)
        .addScaledVector(_normal, camHeight + camDist * sinPitch)

      _targetCamPos.copy(_shipPos).add(_camOffset)

      const camR = _targetCamPos.length()
      if (camR < sphereRadius + 2) {
        _targetCamPos.multiplyScalar((sphereRadius + 2) / camR)
      }

      const posAlpha = 1 - Math.exp(-CAM_POS_LERP * dt)
      camera.position.lerp(_targetCamPos, posAlpha)

      camera.up.copy(_normal)
      _lookAtMatrix.lookAt(camera.position, _shipPos, _normal)
      _targetCamQ.setFromRotationMatrix(_lookAtMatrix)
      camera.quaternion.slerp(_targetCamQ, 1 - Math.exp(-CAM_ROT_LERP * dt))
    }

    // ── Camera-relative rendering uniforms (AFTER camera lerp so both ship
    //    and ocean shaders see the same camera position) ──
    {
      const camPos = camera.position
      const camLen = camPos.length()
      if (camLen > 0) {
        const cdx = camPos.x / camLen, cdy = camPos.y / camLen, cdz = camPos.z / camLen
        const calt = camLen - sphereRadius
        u.uCameraDir.value.set(cdx, cdy, cdz)
        u.uCameraAlt.value = calt
      }
    }

    // ── Update cannonballs ──
    const balls = cannonballsRef.current
    for (let i = balls.length - 1; i >= 0; i--) {
      const ball = balls[i]
      ball.age += dt

      const ballR = ball.position.length()
      if (ballR > 0) {
        ball.velocity.addScaledVector(
          ball.position.clone().normalize(),
          -30 * dt,
        )
      }

      ball.position.addScaledVector(ball.velocity, dt)

      if (ball.age > 5 || ball.position.length() < sphereRadius - 1) {
        balls.splice(i, 1)
      }
    }
  }, -1)

  function fireCannons(
    side: 'port' | 'starboard',
    tierInfo: typeof SHIP_TIERS.sloop,
    shipPos: THREE.Vector3,
    normal: THREE.Vector3,
    fwd: THREE.Vector3,
  ) {
    const rightDir = new THREE.Vector3().crossVectors(fwd, normal)
    const sideDir = rightDir.clone()
    if (side === 'port') sideDir.negate()

    const speed = 120

    for (let i = 0; i < tierInfo.cannonsPerSide; i++) {
      const fwdT = -tierInfo.hullLength * 0.15 + i * (tierInfo.hullLength * 0.5 / Math.max(tierInfo.cannonsPerSide, 1))

      const pos = shipPos.clone()
        .addScaledVector(sideDir, tierInfo.hullWidth * 0.5)
        .addScaledVector(fwd, fwdT)
        .addScaledVector(normal, tierInfo.hullHeight * 0.4)

      const vel = sideDir.clone().multiplyScalar(speed)
        .addScaledVector(normal, speed * 0.3)

      cannonballsRef.current.push({
        id: _cannonballId++,
        position: pos,
        velocity: vel,
        age: 0,
        side,
      })
    }
  }

  if (!activeShip) return null

  const tierInfo = SHIP_TIERS[activeShip.tier]
  const deckY = tierInfo.hullHeight * 0.4
  const postH = tierInfo.hullHeight * 0.6
  const cageSize = 0.35
  const lanternLightY = deckY + postH + cageSize / 2 + 0.05
  const lanternZ = tierInfo.hullLength / 2 - tierInfo.hullLength * 0.03

  return (
    <>
      {/* Ship mesh — CPU buoyancy drives height/pitch/roll uniforms */}
      <ShipMesh tier={activeShip.tier} material={material} />

      {/* Lantern light — follows CPU position (close enough for lighting) */}
      <group ref={lightRef}>
        <pointLight
          position={[0, lanternLightY, -lanternZ]}
          color="#FFA040"
          intensity={2}
          distance={tierInfo.hullLength * 1.5}
          decay={2}
        />
      </group>

      {cannonballsRef.current.map((ball) => (
        <Cannonball key={ball.id} position={ball.position} />
      ))}
    </>
  )
}
